import Phaser from "phaser";

export const STORY_RUNE_STONE_TELEPORT_DURATION_MS = 1700;

const TELEPORT_LINE_OFFSETS = [
  -22, -18, -14, -10, -6, -2,
  0,
  2, 6, 10, 14, 18, 22,
];
const TELEPORT_LINE_COLOR = 0xbef8ff;
const TELEPORT_FLASH_COLOR = 0xe6fcff;

export const createStoryRuneStoneTeleportEffect = (
  scene,
  {
    x = 0,
    y = 0,
    depth = 1000,
    target = null,
    onComplete = null,
  } = {},
) => {
  if (!scene) return null;

  const container = scene.add.container(x, y).setDepth(depth);
  const tweens = [];
  let completed = false;
  const originalTargetAlpha =
    target && typeof target.alpha === "number" ? target.alpha : 1;

  const flash = scene.add
    .ellipse(0, -2, 58, 108, TELEPORT_FLASH_COLOR, 0.14)
    .setBlendMode(Phaser.BlendModes.ADD);
  const baseRing = scene.add
    .ellipse(0, 16, 44, 12, TELEPORT_LINE_COLOR, 0.38)
    .setBlendMode(Phaser.BlendModes.ADD);
  const beam = scene.add
    .rectangle(0, -18, 34, 128, TELEPORT_LINE_COLOR, 0.08)
    .setBlendMode(Phaser.BlendModes.ADD);

  container.add([flash, baseRing, beam]);

  const lines = TELEPORT_LINE_OFFSETS.map((offset, index) => {
    const lane = scene.add.container(offset, 24);
    lane.setBlendMode(Phaser.BlendModes.ADD);
    lane.setAlpha(0);
    lane.setScale(1, 0.28);

    const glow = scene.add
      .rectangle(0, 0, 4, 82, TELEPORT_LINE_COLOR, 0.64)
      .setOrigin(0.5, 1);
    const core = scene.add
      .rectangle(0, 0, 2, 82, 0xffffff, 0.95)
      .setOrigin(0.5, 1);

    lane.add([glow, core]);
    container.add(lane);

    const delay = index * 44;
    const travelDuration = 760 + (index % 4) * 42;

    tweens.push(
      scene.tweens.add({
        targets: lane,
        y: -74,
        alpha: { from: 0, to: 1 },
        scaleY: { from: 0.28, to: 1.14 },
        ease: "Cubic.Out",
        duration: travelDuration,
        delay,
      }),
    );

    tweens.push(
      scene.tweens.add({
        targets: lane,
        alpha: 0,
        ease: "Quad.In",
        duration: 260,
        delay: delay + travelDuration - 120,
      }),
    );

    return lane;
  });

  tweens.push(
    scene.tweens.add({
      targets: flash,
      alpha: { from: 0.08, to: 0.28 },
      scaleX: { from: 0.78, to: 1.12 },
      scaleY: { from: 0.8, to: 1.24 },
      ease: "Sine.Out",
      duration: 420,
      yoyo: true,
      repeat: 1,
    }),
  );

  tweens.push(
    scene.tweens.add({
      targets: beam,
      alpha: { from: 0.04, to: 0.2 },
      scaleY: { from: 0.72, to: 1.22 },
      ease: "Sine.Out",
      duration: 480,
      yoyo: true,
      repeat: 1,
    }),
  );

  tweens.push(
    scene.tweens.add({
      targets: baseRing,
      alpha: { from: 0.3, to: 0.92 },
      scaleX: { from: 0.88, to: 1.5 },
      scaleY: { from: 0.92, to: 1.24 },
      ease: "Sine.Out",
      duration: 520,
      yoyo: true,
      repeat: 1,
    }),
  );

  if (target?.scene) {
    tweens.push(
      scene.tweens.add({
        targets: target,
        alpha: { from: originalTargetAlpha, to: 0.28 },
        ease: "Sine.InOut",
        duration: 130,
        yoyo: true,
        repeat: 7,
      }),
    );
  }

  const cleanup = () => {
    tweens.forEach((tween) => tween?.remove?.());
    delayedComplete?.remove?.(false);
    if (target?.scene) {
      target.setAlpha(originalTargetAlpha);
    }
    container.destroy(true);
  };

  const complete = () => {
    if (completed) return;
    completed = true;
    cleanup();
    if (typeof onComplete === "function") {
      onComplete();
    }
  };

  const delayedComplete = scene.time.delayedCall(
    STORY_RUNE_STONE_TELEPORT_DURATION_MS,
    complete,
  );

  return {
    container,
    cleanup,
    complete,
    sync(next = {}) {
      if (!container?.scene) return;
      if (Number.isFinite(next.x)) container.x = next.x;
      if (Number.isFinite(next.y)) container.y = next.y;
      if (Number.isFinite(next.depth)) container.setDepth(next.depth);
    },
    lines,
  };
};

export const destroyStoryRuneStoneTeleportEffect = (effect) => {
  effect?.cleanup?.();
};

export const syncStoryRuneStoneTeleportEffect = (effect, next = {}) => {
  effect?.sync?.(next);
};
